import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useCustomer } from '../../../lib/customer';
import { theme } from '../../../styles/theme';
import { PortalContainer } from '../PortalShell';
import type { PortalTab } from '../PortalShell';

type Counts = {
  quotes_open: number;
  quotes_to_client: number;
  orders_year: number;
  orders_revenue_year: number;
  deals_open: number;
};

export function PortalPanel({ onTab }: { onTab: (t: PortalTab) => void }) {
  const { customer } = useCustomer();
  const [counts, setCounts] = useState<Counts | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!customer) return;
    (async () => {
      const yearStart = new Date(new Date().getFullYear(), 0, 1).toISOString();
      const [qOpen, qClient, oYear, dOpen, oRev] = await Promise.all([
        supabase.from('quotes').select('*', { count: 'exact', head: true })
          .eq('customer_id', customer.id).eq('kind', 'to_nuled')
          .in('status', ['draft', 'sent']),
        supabase.from('quotes').select('*', { count: 'exact', head: true })
          .eq('customer_id', customer.id).eq('kind', 'to_client'),
        supabase.from('orders').select('*', { count: 'exact', head: true })
          .eq('customer_id', customer.id).gte('created_at', yearStart),
        supabase.from('deals').select('*', { count: 'exact', head: true })
          .eq('customer_id', customer.id).eq('status', 'open'),
        supabase.from('orders').select('total')
          .eq('customer_id', customer.id).eq('currency', 'MXN').gte('created_at', yearStart),
      ]);
      const error = qOpen.error ?? qClient.error ?? oYear.error ?? dOpen.error ?? oRev.error;
      if (error) { setErr(error.message); return; }
      const revenue = (oRev.data ?? []).reduce((s: number, r: any) => s + Number(r.total || 0), 0);
      setCounts({
        quotes_open: qOpen.count ?? 0,
        quotes_to_client: qClient.count ?? 0,
        orders_year: oYear.count ?? 0,
        orders_revenue_year: revenue,
        deals_open: dOpen.count ?? 0,
      });
    })();
  }, [customer]);

  if (!customer) return null;

  return (
    <PortalContainer title="Panel" eyebrow="Resumen ejecutivo">
      {err && <Banner color={theme.err}>Error: {err}</Banner>}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 12,
        marginBottom: 24,
      }}>
        <Stat label="Cotizaciones a NULED vivas"    value={counts?.quotes_open} caption="Borrador / enviadas / vistas" />
        <Stat label="Cotizaciones a mi cliente"     value={counts?.quotes_to_client} caption="Total histórico" />
        <Stat label="Pedidos este año"               value={counts?.orders_year} caption={counts?.orders_revenue_year != null ? `$${counts.orders_revenue_year.toLocaleString('es-MX', { maximumFractionDigits: 0 })} MXN facturados` : ''} />
        <Stat label="Proyectos activos"              value={counts?.deals_open} caption="Deals abiertos" highlight />
      </div>

      <Card>
        <div style={eyebrow}>Acciones rápidas</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
          <ActionBtn primary onClick={() => onTab('cotizaciones')}>+ Cotizar a NULED</ActionBtn>
          <ActionBtn primary onClick={() => onTab('cotizaciones')}>+ Cotizar a mi cliente</ActionBtn>
          <ActionBtn onClick={() => onTab('proyectos')}>+ Registrar proyecto</ActionBtn>
          <ActionBtn onClick={() => onTab('pedidos')}>📦 Reordenar último pedido</ActionBtn>
          <ActionBtn onClick={() => onTab('precios')}>🔍 Buscar SKU</ActionBtn>
        </div>
      </Card>

      <div style={{
        marginTop: 24,
        background: theme.brand,
        color: theme.textOnBrand,
        padding: 24,
        display: 'flex',
        gap: 24,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ ...eyebrow, color: theme.accent }}>Capacitación · Lutron</div>
          <div style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 500, marginTop: 6 }}>
            Líneas certificadas: <strong>{customer.lutron_lines_certified.length}</strong> de 6
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', marginTop: 6 }}>
            {customer.lutron_lines_certified.length > 0 ? (
              <>Vendes: <strong style={{ color: theme.textOnBrand }}>{customer.lutron_lines_certified.map(formatLine).join(' · ')}</strong></>
            ) : 'Aún no estás certificado en ninguna línea Lutron. Inscríbete a una capacitación.'}
          </div>
        </div>
        <button
          onClick={() => onTab('capacitacion')}
          style={{
            background: theme.accent,
            color: theme.brand,
            padding: '12px 24px',
            fontSize: 13,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            border: 'none',
            cursor: 'pointer',
            fontFamily: theme.font.sans,
          }}
        >
          Ver capacitaciones
        </button>
      </div>
    </PortalContainer>
  );
}

function Stat({ label, value, caption, highlight }: { label: string; value: number | undefined; caption: string; highlight?: boolean }) {
  return (
    <div style={{
      background: theme.bg,
      border: `1px solid ${theme.border}`,
      borderTop: highlight ? `3px solid ${theme.accent}` : undefined,
      padding: 20,
    }}>
      <div style={{
        fontSize: 10,
        color: theme.textMuted,
        textTransform: 'uppercase',
        letterSpacing: 1,
        fontWeight: 700,
      }}>
        {label}
      </div>
      <div style={{
        fontFamily: theme.font.head,
        fontSize: 28,
        fontWeight: 600,
        color: theme.brand,
        marginTop: 8,
      }}>
        {value == null ? '—' : value.toLocaleString('es-MX')}
      </div>
      <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>
        {caption}
      </div>
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background: theme.bg,
      border: `1px solid ${theme.border}`,
      padding: 20,
    }}>
      {children}
    </div>
  );
}

function ActionBtn({ children, primary, onClick }: { children: React.ReactNode; primary?: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{
      background: primary ? theme.accent : theme.bg,
      color: theme.brand,
      border: primary ? 'none' : `1px solid ${theme.border}`,
      padding: '10px 18px',
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      cursor: 'pointer',
      fontFamily: theme.font.sans,
      whiteSpace: 'nowrap',
    }}>
      {children}
    </button>
  );
}

function Banner({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <div style={{
      padding: 12,
      background: theme.bg,
      border: `1px solid ${color}`,
      borderLeft: `4px solid ${color}`,
      color,
      marginBottom: 16,
      fontSize: 13,
    }}>
      {children}
    </div>
  );
}

function formatLine(l: string) {
  if (l === 'radiora3') return 'RadioRA 3';
  if (l === 'caseta') return 'Caséta';
  if (l === 'homeworks') return 'HomeWorks';
  return l.charAt(0).toUpperCase() + l.slice(1);
}

const eyebrow: React.CSSProperties = {
  fontSize: 11,
  color: theme.accentDark,
  textTransform: 'uppercase',
  letterSpacing: 1.5,
  fontWeight: 700,
};
